import { lazy, type ComponentType } from "react";

const RELOAD_FLAG = "smb.chunk-reload-attempted";

/**
 * Detects a dynamic import that failed because its chunk no longer exists.
 *
 * Browsers do not give this a distinct error type, so the message is the only
 * signal, and it differs across engines. Matching loosely is the right trade:
 * a false positive costs one page reload, a false negative leaves the user
 * staring at a fatal error on a site that is actually healthy.
 */
function isChunkLoadFailure(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return (
    /failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /importing a module script failed/i.test(message) ||
    /'text\/html' is not a valid javascript mime type/i.test(message)
  );
}

/**
 * A lazily-loaded route that survives a deployment.
 *
 * Code splitting plus immutable filenames means every deploy replaces the
 * chunk names. A browser holding the previous index.html then asks for a file
 * that no longer exists, and React Router surfaces it as a fatal error — which
 * is precisely what happened on the Model check tab after a redeploy.
 *
 * Cache headers stop the *next* visitor hitting it, but cannot help a tab that
 * was already open when the deploy landed. The only fix for that case is to
 * fetch a fresh index.html, so this reloads once.
 *
 * Once, guarded by sessionStorage: if the chunk is genuinely missing rather
 * than merely renamed, reloading on every failure is an infinite refresh loop,
 * which is a considerably worse experience than an error message.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- matches React.lazy's own constraint, so components with props keep them
export function lazyRoute<T extends ComponentType<any>>(
  load: () => Promise<{ default: T }>,
) {
  return lazy(async () => {
    try {
      const mod = await load();
      // Reaching here means the app is coherent again.
      sessionStorage.removeItem(RELOAD_FLAG);
      return mod;
    } catch (error) {
      if (isChunkLoadFailure(error) && !sessionStorage.getItem(RELOAD_FLAG)) {
        sessionStorage.setItem(RELOAD_FLAG, "1");
        window.location.reload();
        // Never settles; the reload replaces the page first.
        return new Promise<{ default: T }>(() => {});
      }
      throw error;
    }
  });
}
